import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useAuth } from '../../../contexts/authWindows';
import { useTranslation } from '../../../contexts/translation';
import { paths } from '../../../utils/core/routes';
import { Loader } from '../Loader/Loader';
import { CheckBox } from '../CheckBox/CheckBox';

type Inputs = {
  username: string;
  password: string;
  persist: boolean;
};

export function LoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signIn, user, isLoading } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<Inputs>();

  useEffect(() => {
    reset({
      username: '',
      password: '',
      persist: false,
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    navigate(paths.HOME);
  }, [user]);

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    if (data.username && data.password) signIn!(data.username, data.password, data.persist);
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          {t('general.action-buttons.log-in')}
        </Typography>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            label={t('general.inputs.username')}
            {...register('username', { required: t('general.messages.error.required-username') })}
            error={Boolean(errors.username)}
            helperText={errors.username?.message}
            autoFocus
          />
          <TextField
            margin="normal"
            required
            fullWidth
            type="password"
            label={t('general.inputs.password')}
            {...register('password', { required: t('general.messages.error.required-password') })}
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
          />
          <CheckBox
            label={t('general.inputs.persist')}
            controllerProps={{
              name: 'persist',
              control: control,
            }}
            errors={errors}
          />
          {isLoading ? (
            <span style={{ marginLeft: '45%' }}>
              <Loader />
            </span>
          ) : (
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
              {t('general.action-buttons.log-in')}
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
}
